import { HttpClient } from '@angular/common/http'
import { Component, OnInit } from '@angular/core'
import { Validators, FormBuilder, FormGroup, AbstractControl } from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { select, Store } from '@ngrx/store'
import { DialCodeItem } from '@tokeet/constants'
import {
  SaveForm,
  selectCurrentAccount,
  Destroyable,
  untilDestroy,
  UpdateUser,
  updateAccount,
} from '@tokeet/tv3-platform'
import { AuthService } from '@tv3/services/auth.service'
import * as lodash from 'lodash'
import { combineLatest, of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'

@Component({
  selector: 'app-phone-number-request-dialog',
  templateUrl: './phone-number-request-dialog.component.html',
  styleUrls: ['./phone-number-request-dialog.component.scss'],
})
export class PhoneNumberRequestDialogComponent extends Destroyable implements OnInit {
  dialCodes: { country: string; cc: string; code: string; codeDisplay: string }[] = []

  accountForm = this.fb.group(
    {
      code: [undefined, [Validators.required]],
      phone: ['', [Validators.required]],
    },
    { asyncValidators: [(ctrl) => this.validatePhoneCtrl(ctrl)] }
  )

  userForm = this.fb.group(
    {
      code: [undefined, [Validators.required]],
      phone: ['', [Validators.required]],
    },
    { asyncValidators: [(ctrl) => this.validatePhoneCtrl(ctrl)] }
  )

  form = this.fb.group({
    account: this.accountForm,
    user: this.userForm,
  })

  countryCode: string
  isLoading = true
  isValidating = false

  constructor(
    public dialogRef: MatDialogRef<PhoneNumberRequestDialogComponent>,
    private store: Store<any>,
    private fb: FormBuilder,
    private auth: AuthService,
    private http: HttpClient
  ) {
    super()
  }

  ngOnInit(): void {
    combineLatest([
      this.store.pipe(select(selectCurrentAccount)),
      this.http.get<DialCodeItem[]>('@webready/public/utils/phone_number/dial_codes'),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([account, dialCodes]) => {
        this.isLoading = false
        this.dialCodes = dialCodes.map(([country, cc, dc, n]: DialCodeItem) => ({
          country,
          cc: lodash.toLower(cc),
          code: dc,
          codeDisplay: `+${dc}`,
        }))

        const isValidCode = (code) => !!lodash.find(this.dialCodes, (t) => t.code == code)

        const accountPhones = lodash.split(lodash.toString(account.phone || '').trim(), ' ')
        const userPhones = lodash.split(lodash.toString(this.auth.user?.phone || '').trim(), ' ')

        if (isValidCode(accountPhones[0])) {
          this.accountForm.patchValue({
            code: accountPhones[0],
            phone: accountPhones[1] || '',
          })
        } else {
          this.accountForm.patchValue({
            phone: accountPhones.join(' '),
          })
        }

        if (isValidCode(userPhones[0])) {
          this.userForm.patchValue({
            code: userPhones[0],
            phone: userPhones[1] || '',
          })
        } else {
          this.userForm.patchValue({
            phone: userPhones.join(' '),
          })
        }
      })
  }

  @SaveForm()
  save(form: FormGroup) {
    const { account, user } = form.getRawValue()
    const userData = this.auth.user
    const userAddress = userData.address
    this.store.dispatch(
      UpdateUser({
        userId: userData.id,
        form: {
          phone: `${user.code} ${user.phone}`,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.primaryEmail,
          role: lodash.toString(lodash.head(userData.roles)),
          address: userAddress?.address,
          city: userAddress?.city,
          country: userAddress?.cc || userAddress?.countryCode,
          state: userAddress?.state,
          postalCode: lodash.toString(userAddress?.postalCode || ''),
          attributes: userData.attributes,
        },
      })
    )

    this.store.dispatch(updateAccount({ account: { phone: `${account.code} ${account.phone}` } as any }))

    this.close()
  }

  validatePhoneCtrl(ctrl: AbstractControl) {
    const data = (ctrl as FormGroup).getRawValue()
    if (!data.code || !data.phone) {
      return
    }

    const countryCode = lodash.find(this.dialCodes, (t) => t.code === data.code)?.cc
    this.isValidating = true
    return this.validate(countryCode, data.phone).pipe(
      map((valid) => {
        this.isValidating = false
        return valid ? undefined : { phone: true }
      })
    )
  }

  validate(countryCode: string, phoneNumber: string) {
    return this.http
      .post<{ valid: boolean }>(`@webready/public/utils/phone_number/validate`, { countryCode, phoneNumber })
      .pipe(
        map(({ valid }) => valid),
        catchError(() => of(true))
      )
  }

  close() {
    this.dialogRef.close()
  }
}
